import { useEffect } from "react";
import useFetch from "hooks/useFetch";
import mixpanel from "mixpanel-browser";
import { useSetRecoilState } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { setAuthHeaders } from "api";
import { userAtom } from "recoil/userAtom";
import { useNavigate } from "react-router-dom";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "./useIsInappWebview";
import { loginBlockModalAtom } from "recoil/loginBlockModalAtom";
import { trackCapiStandardEvent } from "../analytics/metaCapi";

export default function useLogin({
  url,
  idToken,
  code,
  accessToken,
  refreshToken,
  type,
  redirectUri,
}: {
  url: string;
  idToken?: string;
  accessToken?: string;
  refreshToken?: string;
  code?: string;
  type: "google" | "apple" | "kakao";
  redirectUri?: string;
}) {
  let navigate = useNavigate();

  const [loginResult, login] = useFetch({
    url: url,
    requestBody: JSON.stringify({
      type: type,
      ...(idToken && { idToken }),
      ...(code && { code }),
      ...(accessToken && { accessToken }),
      ...(refreshToken && { refreshToken }),
      ...(redirectUri && { redirectUri }),
    }),
  });

  const setPatientState = useSetRecoilState(patientAtom);
  const setUser = useSetRecoilState(userAtom);
  const setLoginBlockModal = useSetRecoilState(loginBlockModalAtom);
  const isInappWebview = useIsInappWebview();

  useEffect(() => {
    if (loginResult) {
      const result = loginResult.result;

      if (isInappWebview && result.role !== "patient") {
        setLoginBlockModal({
          open: true,
          title: "로그인 실패",
          subtitle: "코치는 PC에서 로그인해주세요.",
        });
        return;
      }

      mixpanel.identify(result.trackId);

      setAuthHeaders(result.accessToken);
      sendMessage(
        JSON.stringify({
          event: "value",
          params: {
            accessToken: result.accessToken,
          },
        })
      );

      setUser(result);
      if (result.role === "patient" && result.userId) {
        setPatientState({
          patientId: Number(`${result.userId}`),
          nickname: result.nickname,
        });
      }

      if (isInappWebview && result.isFirstLogin && result.isInsider !== true) {
        sendMessage(
          JSON.stringify({
            event: "send_fb_event",
            params: {
              eventName: "Login",
            },
          })
        );

        trackCapiStandardEvent(
          "CompleteRegistration",
          {
            value: 0,
            currency: "KRW",
            externalId: result.userId,
            email: result.email,
          },
          undefined,
          undefined,
          true
        );

        sendMessage(
          JSON.stringify({
            event: "send_firebase_analytics_event",
            params: {
              eventName: "sign_up",
              eventParams: {
                method: type,
              },
            },
          })
        );
      }

      navigate("/", { replace: true });
    }
  }, [loginResult]);

  return login;
}
